import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const R = 2, DOT_N = 30000;

const LOC = [
    {
        lat: -22.549444, lon: -44.30234,
        name: 'Barra Mansa - RJ',
        address: 'Av. Presidente Kennedy, 3020<br>Ano Bom, Barra Mansa - RJ<br>CEP: 27325-002',
        phones: ['(24) 99215-2426', '(21) 99108-2485'],
        mapEmbed: `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3677.8969429573006!2d-44.30234!3d-22.549444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9bdf7b8c8c8c8c8d%3A0x8c8c8c8c8c8c8c8c!2sAv.%20Presidente%20Kennedy%2C%203020%20-%20Ano%20Bom%2C%20Barra%20Mansa%20-%20RJ%2027325-002!5e0!3m2!1spt-BR!2sbr!4v1234567890" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`
    },
    {
        lat: -20.261085, lon: -40.360132,
        name: 'Cariacica - ES',
        address: 'R. Romualdo Silveira - Vila Cajueiro<br>Cariacica - ES<br>CEP: 29155-270',
        phones: ['(27) 4040-4538'],
        mapEmbed: `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2225.568846903607!2d-40.36013203314202!3d-20.261085354678922!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xb8232d85e31721%3A0xcfcc81bec1b17675!2sR.%20Romualdo%20Silveira%20-%20Vila%20Cajueiro%2C%20Cariacica%20-%20ES%2C%2029155-270!5e0!3m2!1spt-BR!2sbr!4v1776691199178!5m2!1spt-BR!2sbr" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`
    }
];

function ll2v(lat, lon, r) {
    const p = (90 - lat) * Math.PI / 180;
    const t = (lon + 180) * Math.PI / 180;
    return new THREE.Vector3(
        -r * Math.sin(p) * Math.cos(t),
        r * Math.cos(p),
        r * Math.sin(p) * Math.sin(t)
    );
}

/* ── Theme ─────────────────────────────── */
function getSceneBg() {
    return document.documentElement.classList.contains('light-theme') ? 0x080b15 : 0x050510;
}

const scene = new THREE.Scene();
scene.background = new THREE.Color(getSceneBg());

const themeObserver = new MutationObserver(() => {
    scene.background = new THREE.Color(getSceneBg());
});
themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
const container = document.getElementById('globe-container');

const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 500);

const renderer = new THREE.WebGLRenderer({ antialias: false });
renderer.setSize(container.clientWidth, container.clientHeight);
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
container.appendChild(renderer.domElement);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.05;
controls.enableZoom = false;
controls.enableRotate = true;
controls.enablePan = false;
controls.minDistance = 5;
controls.maxDistance = 5;
controls.autoRotate = false;
controls.autoRotateSpeed = 0.4;

const BRASIL_LAT = -14.2350, BRASIL_LON = -51.9253;
const targetDir = ll2v(BRASIL_LAT, BRASIL_LON, 1).normalize();
camera.position.copy(targetDir.clone().multiplyScalar(5));
camera.lookAt(0, 0, 0);

const targetSpherical = new THREE.Spherical().setFromVector3(targetDir.clone().multiplyScalar(5));
let returning = false;

controls.addEventListener('end', () => { returning = true; });

function makeLandMask() {
    const c = document.createElement('canvas');
    c.width = 360; c.height = 180;
    const x = c.getContext('2d');
    x.fillStyle = '#000';
    x.fillRect(0, 0, 360, 180);
    x.fillStyle = '#fff';

    const d = (pts) => {
        x.beginPath();
        pts.forEach(([lo, la], i) => {
            const px = 180 + lo, py = 90 - la;
            i ? x.lineTo(px, py) : x.moveTo(px, py);
        });
        x.closePath(); x.fill();
    };

    // North America
    d([[-130, 72], [-100, 72], [-80, 70], [-65, 60], [-55, 48], [-67, 44], [-75, 35], [-82, 25], [-90, 30], [-97, 26], [-105, 20], [-115, 30], [-120, 34], [-125, 48], [-130, 55], [-145, 60], [-165, 65], [-168, 55], [-145, 58], [-130, 72]]);
    // Central America
    d([[-105, 20], [-97, 18], [-90, 15], [-85, 10], [-80, 8], [-78, 10], [-82, 15], [-88, 18], [-95, 20], [-105, 20]]);
    // South America
    d([[-80, 12], [-60, 10], [-50, 0], [-35, -5], [-35, -15], [-40, -22], [-50, -30], [-60, -40], [-68, -55], [-75, -45], [-70, -18], [-80, -5], [-80, 12]]);
    // Europe
    d([[-10, 36], [0, 43], [-5, 48], [2, 51], [5, 54], [10, 56], [15, 60], [25, 65], [30, 70], [40, 68], [30, 60], [30, 50], [28, 42], [20, 38], [10, 38], [0, 36], [-10, 36]]);
    // UK/Ireland
    d([[-10, 50], [-6, 50], [-5, 52], [-3, 55], [-5, 58], [-2, 58], [0, 55], [2, 52], [0, 51], [-5, 50], [-10, 50]]);
    // Africa
    d([[-17, 15], [-17, 32], [-5, 36], [10, 37], [12, 32], [30, 30], [42, 12], [50, 2], [40, -12], [35, -25], [28, -34], [18, -35], [12, -18], [8, -5], [-5, 5], [-15, 10], [-17, 15]]);
    // Asia
    d([[25, 30], [35, 30], [45, 25], [55, 25], [60, 25], [68, 8], [78, 8], [88, 22], [78, 30], [68, 30], [60, 45], [80, 55], [120, 55], [140, 60], [170, 65], [145, 45], [140, 40], [130, 32], [120, 22], [115, 5], [105, 0], [95, 10], [80, 15], [65, 25], [55, 25], [45, 25], [30, 42], [25, 30]]);
    // Japan
    d([[130, 31], [132, 33], [135, 35], [140, 40], [142, 43], [145, 45], [143, 42], [140, 38], [135, 33], [130, 31]]);
    // Australia
    d([[115, -14], [150, -12], [152, -25], [148, -38], [135, -35], [115, -32], [115, -14]]);
    // New Zealand
    d([[165, -35], [178, -37], [178, -47], [168, -46], [165, -35]]);
    // Greenland
    d([[-55, 60], [-20, 65], [-18, 76], [-40, 82], [-55, 78], [-55, 60]]);
    // Indonesia
    d([[95, -6], [105, -6], [110, -7], [115, -8], [120, -8], [125, -5], [130, -3], [135, -4], [140, -5], [140, -8], [135, -8], [125, -10], [115, -9], [105, -8], [95, -6]]);
    // Madagascar
    d([[43, -12], [50, -16], [50, -25], [44, -25], [43, -12]]);

    return x.getImageData(0, 0, 360, 180);
}

function isLand(lat, lon, mask) {
    const cx = Math.floor(((lon + 180) % 360 + 360) % 360);
    const cy = Math.floor(90 - lat);
    if (cy < 0 || cy >= 180 || cx < 0 || cx >= 360) return false;
    return mask.data[(cy * 360 + cx) * 4] > 128;
}

/* ── Globe Group ───────────────────────── */
const globeGroup = new THREE.Group();
scene.add(globeGroup);

/* Ocean */
const oceanGeo = new THREE.SphereGeometry(R, 64, 64);
const oceanMat = new THREE.MeshBasicMaterial({ color: 0x0a1628, transparent: true, opacity: 0.4 });
globeGroup.add(new THREE.Mesh(oceanGeo, oceanMat));

/* ── Dots ──────────────────────────────── */
const landMask = makeLandMask();
const dotPos = [], dotPhase = [], dotSize = [];

for (let i = DOT_N; i >= 0; i--) {
    const phi = Math.acos(-1 + (2 * i) / DOT_N);
    const theta = Math.sqrt(DOT_N * Math.PI) * phi;
    const s = 1.005;
    const x = s * R * Math.sin(phi) * Math.cos(theta);
    const y = s * R * Math.cos(phi);
    const z = s * R * Math.sin(phi) * Math.sin(theta);
    const lat = 90 - Math.acos(y / (s * R)) * 180 / Math.PI;
    const lon = Math.atan2(z, -x) * 180 / Math.PI - 180;
    if (isLand(lat, lon, landMask)) {
        dotPos.push(x, y, z);
        dotPhase.push(Math.random());
        dotSize.push(0.008 + Math.random() * 0.007);
    }
}

const dotGeo = new THREE.BufferGeometry();
dotGeo.setAttribute('position', new THREE.Float32BufferAttribute(dotPos, 3));
dotGeo.setAttribute('aPhase', new THREE.Float32BufferAttribute(dotPhase, 1));
dotGeo.setAttribute('aSize', new THREE.Float32BufferAttribute(dotSize, 1));

const dotMat = new THREE.ShaderMaterial({
    uniforms: {
        uTime: { value: 0 },
        uScale: { value: container.clientHeight / (2 * Math.tan(camera.fov * Math.PI / 360)) },
        uColor: { value: new THREE.Color(0x4dd0e1) }
    },
    vertexShader: `
            attribute float aPhase;
            attribute float aSize;
            uniform float uTime;
            uniform float uScale;
            varying float vOp;
            void main(){
            vOp = 0.35 + 0.65 * abs(sin(uTime * 1.5 + aPhase * 6.2832));
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = aSize * uScale / -mv.z;
            gl_Position = projectionMatrix * mv;
            }
        `,
    fragmentShader: `
            uniform vec3 uColor;
            varying float vOp;
            void main(){
            float d = length(gl_PointCoord - vec2(0.5));
            if(d > 0.5) discard;
            float a = vOp * smoothstep(0.5, 0.05, d);
            gl_FragColor = vec4(uColor, a);
            }
        `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
});

globeGroup.add(new THREE.Points(dotGeo, dotMat));

/* ── Markers ───────────────────────────── */
const markers = LOC.map((loc, i) => {
    const pos = ll2v(loc.lat, loc.lon, R * 1.01);
    const geo = new THREE.SphereGeometry(0.03, 16, 16);
    const mat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    globeGroup.add(mesh);

    const ringGeo = new THREE.RingGeometry(0.04, 0.065, 32);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.copy(pos);
    ring.lookAt(0, 0, 0);
    globeGroup.add(ring);

    return { mesh, ring, loc };
});

/* ── Beams ──────────────────────────────── */
const BEAM_H = 0.5;
const beams = markers.map((marker) => {
    const pos = marker.mesh.position.clone();
    const normal = pos.clone().normalize();

    const geo = new THREE.CylinderGeometry(0.001, 0.005, BEAM_H, 8, 1, true);
    const mat = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uColor: { value: new THREE.Color(0x00ffff) },
            uHeight: { value: BEAM_H }
        },
        vertexShader: `
            varying float vY;
            void main(){
                vY = position.y;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float uTime;
            uniform vec3 uColor;
            uniform float uHeight;
            varying float vY;
            void main(){
                float t = (vY + uHeight * 0.5) / uHeight;
                float fade = pow(1.0 - t, 2.0);
                float pulse = 0.5 + 0.5 * sin(uTime * 2.0);
                float a = fade * pulse * 0.6;
                gl_FragColor = vec4(uColor * (0.5 + 0.5 * fade), a);
            }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos.clone().add(normal.clone().multiplyScalar(BEAM_H / 2)));
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
    globeGroup.add(mesh);

    return { mesh, mat };
});

/* ── 2D Overlays (Google Maps cards) ───── */
const PIN_POS = [
    { side: 'left' },
    { side: 'right' }
];
const svgLines = document.getElementById('globe-lines');

const overlays = markers.map((marker, i) => {
    const pos = marker.mesh.position.clone();
    const normal = pos.clone().normalize();
    const tip = pos.clone().add(normal.clone().multiplyScalar(BEAM_H));

    const phoneHtml = marker.loc.phones
        .map(p => `<a href="tel:${p.replace(/[^0-9+]/g, '')}" class="globe-pin-phone">${p}</a>`)
        .join(' / ');

    const el = document.createElement('div');
    const textEl = document.createElement('div');

    el.className = `globe-pin globe-pin--${PIN_POS[i].side}`;
    el.innerHTML = `
        <div class="globe-pin-card">
            <div class="globe-pin-name globe-heading"> CATIVO ${marker.loc.name}</div>
            <div class="globe-pin-details">
                <div class="globe-pin-row"><i>&#xf276;</i> <span>${marker.loc.address}</span></div>
                <div class="globe-pin-row"><i>&#xf2A0;</i>  <span>${phoneHtml}</span></div>
            </div>
            <div class="globe-pin-map">${marker.loc.mapEmbed}</div>
        </div>
    `;
    container.appendChild(el);
    container.appendChild(textEl);

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('stroke', 'rgba(0,255,255,0.35)');
    line.setAttribute('stroke-width', '1');
    svgLines.appendChild(line);

    return { el, tip, line };
});

/* ── Arc ───────────────────────────────── */
function buildArc() {
    const p0 = ll2v(LOC[0].lat, LOC[0].lon, R);
    const p3 = ll2v(LOC[1].lat, LOC[1].lon, R);
    const omega = p0.angleTo(p3);
    const sinO = Math.sin(omega);

    let curve;
    if (sinO < 0.001) {
        curve = new THREE.LineCurve3(p0, p3);
    } else {
        const cp1 = new THREE.Vector3()
            .addScaledVector(p0, Math.sin(0.75 * omega) / sinO)
            .addScaledVector(p3, Math.sin(0.25 * omega) / sinO);
        const cp2 = new THREE.Vector3()
            .addScaledVector(p0, Math.sin(0.25 * omega) / sinO)
            .addScaledVector(p3, Math.sin(0.75 * omega) / sinO);
        const h = R + p0.distanceTo(p3) * 0.55;
        cp1.normalize().multiplyScalar(h);
        cp2.normalize().multiplyScalar(h);
        curve = new THREE.CubicBezierCurve3(p0, cp1, cp2, p3);
    }

    const tubSeg = 120, radSeg = 8;
    const geo = new THREE.TubeGeometry(curve, tubSeg, 0.004, radSeg, false);

    const vertsPerRing = radSeg + 1;
    const totalRings = tubSeg + 1;
    const progressArr = new Float32Array(geo.attributes.position.count);
    for (let i = 0; i < progressArr.length; i++) {
        progressArr[i] = Math.floor(i / vertsPerRing) / (totalRings - 1);
    }
    geo.setAttribute('aProgress', new THREE.Float32BufferAttribute(progressArr, 1));

    const mat = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uColorA: { value: new THREE.Color(0x00ffff) },
            uColorB: { value: new THREE.Color(0x7c4dff) }
        },
        vertexShader: `
            attribute float aProgress;
            varying float vT;
            void main(){
                vT = aProgress;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
            `,
        fragmentShader: `
            uniform float uTime;
            uniform vec3 uColorA;
            uniform vec3 uColorB;
            varying float vT;
            void main(){
                vec3 col = mix(uColorA, uColorB, vT);
                float pulse = sin(vT * 6.2832 * 2.0 - uTime * 3.0) * 0.5 + 0.5;
                float fade = smoothstep(0.0, 0.08, vT) * smoothstep(1.0, 0.92, vT);
                float a = pulse * fade * 0.85;
                gl_FragColor = vec4(col * (0.4 + pulse * 0.6), a);
            }
            `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
    });

    const mesh = new THREE.Mesh(geo, mat);
    globeGroup.add(mesh);
    return { mesh, geo, mat, totalVerts: geo.attributes.position.count };
}

const arc = buildArc();

/* ── Stars ─────────────────────────────── */
const starN = 2000;
const starArr = new Float32Array(starN * 3);
for (let i = 0; i < starN; i++) {
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    const rr = 40 + Math.random() * 60;
    starArr[i * 3] = rr * Math.sin(ph) * Math.cos(th);
    starArr[i * 3 + 1] = rr * Math.sin(ph) * Math.sin(th);
    starArr[i * 3 + 2] = rr * Math.cos(ph);
}
const starGeo = new THREE.BufferGeometry();
starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starArr, 3));
scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.08, sizeAttenuation: true })));

/* ── Atmosphere Glow ───────────────────── */
const atmosGeo = new THREE.SphereGeometry(R * 1.12, 64, 64);
const atmosMat = new THREE.ShaderMaterial({
    vertexShader: `
            varying vec3 vNorm;
            void main(){
            vNorm = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
    fragmentShader: `
            varying vec3 vNorm;
            void main(){
            float i = pow(0.65 - dot(vNorm, vec3(0.0, 0.0, 1.0)), 2.5);
            gl_FragColor = vec4(0.2, 0.5, 1.0, i * 0.35);
            }
        `,
    transparent: true,
    side: THREE.BackSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending
});
globeGroup.add(new THREE.Mesh(atmosGeo, atmosMat));

/* ── Animation ─────────────────────────── */
const clock = new THREE.Clock();

function animate() {
    requestAnimationFrame(animate);
    const t = clock.getElapsedTime();

    dotMat.uniforms.uTime.value = t;
    arc.mat.uniforms.uTime.value = t;
    beams.forEach(b => { b.mat.uniforms.uTime.value = t; });

    markers.forEach((m, i) => {
        const s = 1 + 0.35 * Math.sin(t * 3 + i * Math.PI);
        m.mesh.scale.setScalar(s);
        m.ring.scale.setScalar(s);
        m.ring.material.opacity = 0.25 + 0.35 * Math.sin(t * 2 + i * Math.PI);
    });

    if (returning) {
        const current = new THREE.Spherical().setFromVector3(camera.position);
        const distTheta = current.theta - targetSpherical.theta;
        const distPhi = current.phi - targetSpherical.phi;

        if (Math.abs(distTheta) < 0.005 && Math.abs(distPhi) < 0.005) {
            camera.position.copy(
                new THREE.Vector3().setFromSpherical(targetSpherical)
            );
            returning = false;
        } else {
            current.theta -= distTheta * 0.06;
            current.phi -= distPhi * 0.06;
            current.radius = 5;
            camera.position.copy(
                new THREE.Vector3().setFromSpherical(current)
            );
        }
        camera.lookAt(0, 0, 0);
    }

    /* Project beam tips → draw SVG lines to fixed cards */
    const hw = container.clientWidth / 2, hh = container.clientHeight / 2;
    overlays.forEach(({ el, tip, line }) => {
        const world = tip.clone().applyMatrix4(globeGroup.matrixWorld);
        const ndc = world.clone().project(camera);

        if (ndc.z > 1) {
            el.classList.add('hidden');
            line.setAttribute('stroke', 'rgba(0,255,255,0)');
        } else {
            el.classList.remove('hidden');
            const sx = (ndc.x * hw) + hw;
            const sy = -(ndc.y * hh) + hh;

            const rect = el.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const cardX = rect.left - containerRect.left + rect.width / 2;
            const cardY = rect.top - containerRect.top + rect.height;

            line.setAttribute('x1', sx);
            line.setAttribute('y1', sy);
            line.setAttribute('x2', cardX);
            line.setAttribute('y2', cardY);
            line.setAttribute('stroke', 'rgba(0,255,255,0.35)');
        }
    });

    controls.update();
    renderer.render(scene, camera);
}
animate();

/* ── Resize ────────────────────────────── */
addEventListener('resize', () => {
    const w = container.clientWidth, h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    dotMat.uniforms.uScale.value = h / (2 * Math.tan(camera.fov * Math.PI / 360));
});
